import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { DatePicker, message, ConfigProvider, theme as antdTheme } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { getPeriodoById, savePeriodo } from "@/services/periodoLetivo";

dayjs.locale("pt-br");

const defaultValues = {
  nome: "",
  dataInicio: null,
  dataFim: null,
  ativo: true,
};

export default function CadastroPeriodoLetivo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (isEdit && id) {
      getPeriodoById(id).then((res) => {
        if (res.status === 200) {
          const periodo = res.data;
          const dataInicio = periodo.dataInicio || null;
          const dataFim = periodo.dataFim || null;
          reset({
            ...defaultValues,
            ...periodo,
            dataInicio: dataInicio ? dayjs(dataInicio) : null,
            dataFim: dataFim ? dayjs(dataFim) : null,
            ativo: periodo.ativo !== undefined ? periodo.ativo : true,
          });
        }
      });
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset]);

  const onSubmit = async (data: any) => {
    try {
      const payload = {
        ...data,
        id: id || undefined,
        dataInicio: data.dataInicio ? data.dataInicio.format("YYYY-MM-DD") : null,
        dataFim: data.dataFim ? data.dataFim.format("YYYY-MM-DD") : null,
      };
      const response = await savePeriodo(payload);
      if (response.status === 200 || response.status === 201) {
        message.success("Período letivo salvo com sucesso!");
        navigate("/periodoletivo");
      }
    } catch (error: any) {
      message.error("Erro ao salvar período letivo");
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Período Letivo" : "Cadastro de Período Letivo"}</span>
        </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="flex items-center gap-2 mt-6">
            <Input
              className="h-4 w-4"
              type="checkbox"
              id="ativo"
              {...register("ativo")}
              checked={watch("ativo")}
              onChange={e => setValue("ativo", e.target.checked)}
            />
            <label htmlFor="ativo">Ativo</label>
          </div>
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div>
            <label>Data Inicial *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <DatePicker
                format="DD/MM/YYYY"
                className="w-full text-sm font-medium h-10"
                value={watch("dataInicio")}
                onChange={date => setValue("dataInicio", date, { shouldValidate: true })}
                placeholder="Selecione a data inicial"
              />
            </ConfigProvider>
            {errors.dataInicio && <span className="text-red-600">Data inicial é obrigatória</span>}
          </div>
          <div>
            <label>Data Final *</label>
            <ConfigProvider theme={{ algorithm: [antdTheme.darkAlgorithm, antdTheme.compactAlgorithm] }}>
              <DatePicker
                format="DD/MM/YYYY"
                className="w-full text-sm font-medium h-10"
                value={watch("dataFim")}
                onChange={date => setValue("dataFim", date, { shouldValidate: true })}
                placeholder="Selecione a data final"
              />
            </ConfigProvider>
            {errors.dataFim && <span className="text-red-600">Data final é obrigatória</span>}
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/periodoletivo")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
} 